import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useToastStore } from '../store/toastStore';
import { referralAPI } from '../api/client';
import { track } from '../utils/analytics';
import Layout from '../components/Layout';

export default function Referral() {
  const { user } = useAuthStore();
  const showToast = useToastStore(s => s.showToast);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.userId) return;

    // my-code 호출 시 자동 생성됨 → dashboard에서 코드 포함 정보 조회
    Promise.all([
      referralAPI.getMyCode(user.userId),
      referralAPI.getDashboard(user.userId),
    ]).then(([codeRes, dashRes]) => {
      if (dashRes.success) {
        setData({
          ...dashRes.data,
          code: dashRes.data.code || codeRes.data?.code,
        });
      }
    }).catch((err) => {
      console.error('[Referral] Failed to load:', err);
    }).finally(() => setLoading(false));
  }, [user?.userId]);

  const copyCode = async () => {
    if (!data?.code) return;
    track('referral-shared', { method: 'copy' });
    try {
      await navigator.clipboard.writeText(data.code);
      showToast('코드가 복사됐어요', 'success');
    } catch {
      showToast('복사에 실패했어요', 'error');
    }
  };

  const shareLink = async () => {
    if (!data?.code) return;
    const url = `https://tayobus.app/r/${data.code}`;
    const shareData = {
      title: '타요버스 초대',
      text: `타요버스에서 내 코드 ${data.code} 입력하면 1주 무료! `,
      url,
    };

    if (navigator.share) {
      track('referral-shared', { method: 'share' });
      try {
        await navigator.share(shareData);
      } catch { /* user cancelled */ }
    } else {
      track('referral-shared', { method: 'copy' });
      try {
        await navigator.clipboard.writeText(url);
        showToast('링크가 복사됐어요', 'success');
      } catch {
        showToast('복사에 실패했어요', 'error');
      }
    }
  };

  if (loading) {
    return (
      <Layout title="친구 초대" backTo="/dashboard" showBottomNav>
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-[3px] border-tayo/20 border-t-tayo rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const hasAccess = data?.accessUntil && new Date(data.accessUntil) > new Date();
  const accessDays = hasAccess
    ? Math.ceil((new Date(data.accessUntil) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;
  const slotsUsed = data?.usedCount || 0;
  const slotsMax = data?.maxUses || 3;

  return (
    <Layout title="친구 초대" backTo="/dashboard" showBottomNav>
      {/* Trial 상태 배너 */}
      {hasAccess && (
        <div className="bg-gradient-to-br from-tayo via-tayo to-tayo-dark rounded-2xl p-5 mb-4 animate-fade-up relative overflow-hidden">
          {/* 배경 장식 */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-12 translate-x-12" />
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/5 rounded-full translate-y-8 -translate-x-8" />
          <div className="relative">
            <p className="text-sm font-medium text-white/70">타요패스 남은 기간</p>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="text-4xl font-extrabold text-white">{accessDays}</span>
              <span className="text-lg font-bold text-white/70">일</span>
            </div>
            <p className="text-xs text-white/50 mt-1.5">
              {new Date(data.accessUntil).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })}까지
            </p>
          </div>
        </div>
      )}

      {/* 내 초대 코드 */}
      <div className="bg-white rounded-2xl shadow-card border border-border p-5 mb-4 animate-fade-up stagger-1">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-tayo-light rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-tayo" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
          </div>
          <h3 className="text-sm font-bold text-charcoal">내 초대 코드</h3>
        </div>

        {/* 코드 디스플레이 */}
        <div className="bg-snow rounded-2xl px-5 py-4 mb-4">
          <p className="text-center text-[28px] font-extrabold tracking-[0.3em] text-charcoal pl-[0.3em] select-all">
            {data?.code || '------'}
          </p>
        </div>

        {/* 액션 버튼들 */}
        <div className="grid grid-cols-2 gap-2.5">
          <button
            onClick={copyCode}
            className="btn-press flex items-center justify-center gap-2 py-3 bg-tayo-light rounded-xl text-tayo font-semibold text-sm hover:bg-tayo/15 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            코드 복사
          </button>
          <button
            onClick={shareLink}
            className="btn-press flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-tayo to-tayo-dark rounded-xl text-white font-semibold text-sm hover:shadow-md transition-shadow"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            링크 공유
          </button>
        </div>

        <p className="text-xs text-sub-text text-center mt-3.5">
          친구가 이 코드를 입력하고 스케줄을 등록하면 <span className="text-tayo font-semibold">서로 1주 무료!</span>
        </p>
      </div>

      {/* 초대 현황 */}
      <div className="bg-white rounded-2xl shadow-card border border-border p-5 mb-4 animate-fade-up stagger-2">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-mint" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h3 className="text-sm font-bold text-charcoal">초대 현황</h3>
          <span className="ml-auto text-sm font-bold text-tayo">{slotsUsed}/{slotsMax}</span>
        </div>

        {/* 프로그레스 바 */}
        <div className="flex gap-1.5 mb-1">
          {Array.from({ length: slotsMax }).map((_, i) => (
            <div key={i} className="flex-1 h-2 rounded-full overflow-hidden bg-gray-100">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  i < slotsUsed ? 'bg-gradient-to-r from-tayo to-tayo-dark w-full' : 'w-0'
                }`}
              />
            </div>
          ))}
        </div>

        {slotsUsed === 0 ? (
          <p className="text-sm text-sub-text text-center py-6">
            아직 초대한 친구가 없어요
          </p>
        ) : (
          /* 사용 내역 리스트 */
          <div className="mt-4 space-y-2">
            {(data?.uses || []).map((use, i) => (
              <div key={use.id} className="flex items-center gap-3 p-3 bg-snow rounded-xl animate-fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                  use.reward_granted
                    ? 'bg-gradient-to-br from-tayo to-tayo-dark text-white'
                    : 'bg-gray-100 text-sub-text'
                }`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-charcoal">
                    {use.reward_granted ? '스케줄 등록 완료' : '가입 완료'}
                  </p>
                  <p className="text-xs text-sub-text">
                    {new Date(use.used_at).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                  </p>
                </div>
                {use.reward_granted ? (
                  <span className="flex-shrink-0 text-xs font-bold text-tayo bg-tayo-light px-2.5 py-1 rounded-lg">
                    +1주 적립
                  </span>
                ) : (
                  <span className="flex-shrink-0 text-xs text-sub-text bg-gray-100 px-2.5 py-1 rounded-lg" title="친구가 스케줄을 등록하면 보상이 확정됩니다">
                    스케줄 등록 대기
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 안내 */}
      <div className="bg-white rounded-2xl shadow-card border border-border p-5 animate-fade-up stagger-3">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-sm font-bold text-charcoal">이용 안내</h3>
        </div>
        <ul className="space-y-2.5 text-xs text-sub-text leading-relaxed">
          <li className="flex gap-2">
            <span className="text-tayo font-bold mt-px">1</span>
            <span>내 코드를 친구에게 공유해요</span>
          </li>
          <li className="flex gap-2">
            <span className="text-tayo font-bold mt-px">2</span>
            <span>친구가 코드를 입력하면 <span className="text-charcoal font-semibold">1주 무료</span>를 받아요</span>
          </li>
          <li className="flex gap-2">
            <span className="text-tayo font-bold mt-px">3</span>
            <span>친구가 스케줄을 등록하면 나도 <span className="text-charcoal font-semibold">1주 무료</span>가 적립돼요</span>
          </li>
          <li className="flex gap-2">
            <span className="text-sub-text/60 font-bold mt-px">*</span>
            <span>1개의 코드는 최대 {slotsMax}명까지 사용할 수 있어요</span>
          </li>
        </ul>
      </div>
    </Layout>
  );
}
